// components/members/MembersFullPage.js
import React, { useState, useCallback } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import MemberCard from './MemberCard';
import IntegratedSearchNavBar from '../navigation/IntegratedSearchNavBar';
import useMembersPageData from '../../hooks/useMembersPageData';
import { MEMBER_TYPES } from '../../utils/membersConfig';
import './MembersFullPage.css';

const MembersFullPage = ({
  memberType = MEMBER_TYPES.HOUSE,
  title,
}) => {
  const {
    data,
    loading,
    loadingMore,
    error,
    hasMore,
    loadMore,
    search,
    clearSearch,
    searchTerm,
    config,
  } = useMembersPageData(memberType);

  // Handle search changes from the navbar
  const handleSearchChange = useCallback((query) => {
    if (query.trim() === '') {
      clearSearch();
    } else {
      search(query);
    }
  }, [search, clearSearch]);

  // Get placeholder text based on member type
  const getPlaceholder = () => {
    switch (memberType) {
      case MEMBER_TYPES.HOUSE:
        return 'Search Representatives...';
      case MEMBER_TYPES.SENATE:
        return 'Search Senators...';
      case MEMBER_TYPES.PRESIDENT:
        return 'Search Presidents...';
      case MEMBER_TYPES.VICE_PRESIDENT:
        return 'Search Vice Presidents...';
      case MEMBER_TYPES.JUSTICE:
        return 'Search Justices...';
      default:
        return 'Search members...';
    }
  };

  return (
    <div>
      {/* Integrated Search NavBar */}
      <IntegratedSearchNavBar
        searchQuery={searchTerm}
        onSearchChange={handleSearchChange}
        currentCountry="us"
        placeholder={getPlaceholder()}
      />
      <br></br>
      <br></br>
      {/* Loading State */}
      {loading && (
        <div className="d-flex justify-content-center align-items-center" style={{ padding: '40px' }}>
          <Spinner id='spinner' animation="grow" variant="light" />
        </div>
      )}

      {/* Error State */}
      {error && (
        <div id='fullBillFeed'>
          <p className="members-message">Error loading members. Please try again.</p>
        </div>
      )}

      {/* Members List */}
      {!loading && !error && data && (
        <div id='fullBillFeed'>
          {data.length === 0 ? (
            <p className="members-message members-message--center">
              No members found{searchTerm ? ` for "${searchTerm}"` : ''}.
            </p>
          ) : (
            data.map((member, index) => (
              <MemberCard
                key={member.bio_guide_id || member.president_num || member.full_name || index}
                member={member}
                memberType={memberType}
                variant="full"
                containerId="feedContainerNormalView"
              />
            ))
          )}
        </div>
      )}

      {/* Load More Button */}
      {!loading && !error && data && data.length > 0 && (
        <div id='loadMoreSection' style={{ textAlign: 'center', padding: '20px'}}>
          <Button
            id='searchViewMoreButtonMobile'
            onClick={loadMore}
            disabled={loadingMore || !hasMore}
          >
            {loadingMore ? (
              <Spinner id='spinner' animation="grow" variant="light" size="sm" />
            ) : hasMore ? (
              'More'
            ) : (
              'No More Results'
            )}
          </Button>
          <br></br>  <br></br>  <br></br>
        </div>
      )}
    </div>
  );
};

// Convenience components for specific pages
export const HouseOfRepsMembersPage = () => (
  <MembersFullPage 
    memberType={MEMBER_TYPES.HOUSE} 
    title="House of Representatives" 
  />
);

export const SenateMembersPage = () => (
  <MembersFullPage 
    memberType={MEMBER_TYPES.SENATE} 
    title="Senate" 
  />
);

export const PresidentsPage = () => (
  <MembersFullPage 
    memberType={MEMBER_TYPES.PRESIDENT} 
    title="Presidents" 
  />
);

export const VicePresidentsPage = () => (
  <MembersFullPage 
    memberType={MEMBER_TYPES.VICE_PRESIDENT} 
    title="Vice Presidents" 
  />
);

export const JusticesPage = () => (
  <MembersFullPage 
    memberType={MEMBER_TYPES.JUSTICE} 
    title="Supreme Court Justices" 
  />
);

export default MembersFullPage;